import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader } from "@/components/shared";
import Layout from "@/components/Layout";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import AuthCallback from "@/pages/auth/AuthCallback";

import Dashboard from "@/pages/Dashboard";
import Assessments from "@/pages/Assessments";
import AssessmentEditor from "@/pages/AssessmentEditor";
import Reports from "@/pages/Reports";
import ReportDetail from "@/pages/ReportDetail";
import ValidationQueue from "@/pages/ValidationQueue";
import ValidationDetail from "@/pages/ValidationDetail";
import Followups from "@/pages/Followups";
import FollowupDetail from "@/pages/FollowupDetail";
import UserManagement from "@/pages/admin/UserManagement";
import AssessmentConfig from "@/pages/admin/AssessmentConfig";
import AuditLog from "@/pages/admin/AuditLog";
import ComparePage from "@/pages/ComparePage";

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loader label="Authenticating" />;
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader label="Loading" />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Detect OAuth callback synchronously (fragment), before other routing runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/assessments" element={<Protected roles={["tac", "am", "pd", "admin"]}><Assessments /></Protected>} />
      <Route path="/assessments/:id" element={<Protected roles={["tac", "am", "pd", "admin"]}><AssessmentEditor /></Protected>} />
      <Route path="/assessments/:id/compare" element={<Protected roles={["tac", "validator", "am", "pd", "admin"]}><ComparePage /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/reports/:id" element={<Protected><ReportDetail /></Protected>} />
      <Route path="/validation" element={<Protected roles={["validator", "am", "admin"]}><ValidationQueue /></Protected>} />
      <Route path="/validation/:id" element={<Protected roles={["validator", "am", "pd", "admin"]}><ValidationDetail /></Protected>} />
      <Route path="/followups" element={<Protected><Followups /></Protected>} />
      <Route path="/followups/:id" element={<Protected><FollowupDetail /></Protected>} />
      <Route path="/admin/users" element={<Protected roles={["admin"]}><UserManagement /></Protected>} />
      <Route path="/admin/config" element={<Protected roles={["admin"]}><AssessmentConfig /></Protected>} />
      <Route path="/admin/audit" element={<Protected roles={["admin", "am", "pd"]}><AuditLog /></Protected>} />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <BrandingProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrandingProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
